/**
 * @lodex-pro/shared/audit/job-creation-events — DEC-316.
 *
 * The audit events that record "this job was created", and the words a human
 * reads for each one. ONE list, because three places need exactly the same set
 * and drifting between them is the failure this module exists to prevent:
 *
 *   1. every job-creation path writes one of these `event_type` values, in the
 *      same transaction as its `INSERT INTO jobs`;
 *   2. the job-detail audit endpoint (`routes/jobs/audit.ts`) admits this set
 *      when it projects office-side `audit_events` rows onto a job's timeline;
 *   3. the dispatcher timeline (`JobDetailModal.tsx`) renders the LABEL — the
 *      timeline falls back to printing the raw `event_type` for anything it has
 *      no text for, so a missing label puts `JOB_CREATED_AUTO_HAUL` on screen.
 *
 * Adding a seventh creation path therefore means adding one entry HERE, and
 * `job-creation-events.test.ts` fails until the route and the UI agree with it.
 *
 * WHY THE NAMES ARE UNEVEN. Four of these were already written by existing
 * paths (DEC-316 only added `JOB_CREATED` and reused `JOB_CREATED_DRIVER_LOAD`
 * for the sync-minted twin). Renaming the others would strand every row already
 * in the table — `event_type` is free text and nothing rewrites history — so the
 * set is matched by MEMBERSHIP, never by a `LIKE 'JOB_CREATED%'` prefix that
 * would both miss `OPERATOR_API_HAUL_REQUEST_CREATED` and silently swallow some
 * future `JOB_CREATED_SOMETHING_ELSE` nobody meant to put on the timeline.
 *
 * Labels are Corbin's wording (2026-10-10).
 *
 * Spec: master spec §32 (audit trail), DEC-316.
 */

/** Creation `event_type` → the words the dispatcher timeline shows. */
export const JOB_CREATION_EVENT_LABELS = {
  /** POST /v1/jobs — a dispatcher, dispatch manager, tenant admin or any other
   *  office role with CREATE_JOB. The actor is `jobs.created_by_id`. */
  JOB_CREATED: 'Job created by dispatch',
  /** POST /v1/jobs/driver-load, and the parent job the sync receiver mints for
   *  a load a driver created offline — the same act, so the same name. */
  JOB_CREATED_DRIVER_LOAD: 'Load created by driver',
  /** POST /v1/jobs/foreman-request — an operator-side foreman at the wellsite. */
  JOB_CREATED_FOREMAN_REQUEST: "Haul requested by the operator's foreman",
  /** Operator Public API haul request (DEC-293). No human actor: `actor_id` is
   *  NULL and the credential is named in the payload. */
  OPERATOR_API_HAUL_REQUEST_CREATED: 'Haul requested via the operator API',
  /** OPP PR 12a (0222) — an operator user turned the load in through the portal (or, from
   *  PR 12b, the phone). `actor_type = 'CUSTOMER'`, `actor_id` = that user. */
  OPERATOR_PORTAL_HAUL_REQUEST_CREATED: 'Haul requested in the operator portal',
  /** The auto-haul sweeper. `actor_type = 'SYSTEM'`, `actor_id` NULL. */
  JOB_CREATED_AUTO_HAUL: 'Job created automatically',
} as const satisfies Record<string, string>;

export type JobCreationEventType = keyof typeof JOB_CREATION_EVENT_LABELS;

/** The set, for the audit endpoint's `event_type IN (…)` and for tests. */
export const JOB_CREATION_EVENT_TYPES = Object.keys(
  JOB_CREATION_EVENT_LABELS,
) as readonly JobCreationEventType[];

/** The label for a creation event, or null if this is not a creation event. */
export function jobCreationEventLabel(eventType: string): string | null {
  return eventType in JOB_CREATION_EVENT_LABELS
    ? JOB_CREATION_EVENT_LABELS[eventType as JobCreationEventType]
    : null;
}
